// What an app is allowed to say when something breaks.
//
// Two problems, and the second is the one that would embarrass us.
//
// A React render error takes the whole tree down and leaves a blank page.
// On the customer's screen that is the worst failure this product has:
// somebody opened a link to find out where their parcel is and got
// nothing at all, with no way to tell whether the run ended, the link is
// wrong, or the page is broken.
//
// The obvious fix makes it worse. Error text almost always ends up
// carrying the URL — a stack frame, a fetch message, `location.href` in a
// "context" field — and **the capability is in the URL**. A tracking link
// is a publish-free read capability for one vehicle's position: printing
// it on screen puts it in a screenshot, and sending it to any error
// reporter puts it on somebody else's servers, which is the one thing
// this product promises does not happen.
//
// So error text is scrubbed here, once, and both apps use it. It is
// deliberately blunt: anything that looks like a URL, a fragment, or a
// long base64url run is replaced rather than trimmed, because a partially
// redacted capability is still 78 bytes of somebody's key with a hole in
// it.

/** What a redacted capability is replaced with, so the loss is visible. */
const REDACTED = "[redacted]";

/**
 * A run of base64url long enough to be a capability (78 bytes → 104
 * chars) or a recipient card (11 bytes → 15). 15 is the floor, and
 * ordinary words do not reach it without a separator.
 */
const SECRET_RUN = /[A-Za-z0-9_-]{15,}/gu;
/** Anything with a scheme, and anything after a fragment marker. */
const URLISH = /\b[a-z][a-z0-9+.-]*:\/\/\S*/giu;
const FRAGMENT = /#\S+/gu;

/**
 * Error text with anything capability-shaped removed.
 *
 * Never returns an empty string: "something went wrong and we will not
 * say what" is worse than a scrubbed sentence, because it reads as a
 * cover-up rather than a redaction.
 */
export function safeErrorText(error: unknown): string {
  const raw = error instanceof Error
    ? error.message
    : typeof error === "string"
      ? error
      : "";
  const scrubbed = raw
    .replace(URLISH, REDACTED)
    .replace(FRAGMENT, REDACTED)
    .replace(SECRET_RUN, REDACTED)
    .trim();
  return scrubbed || "No further detail is available.";
}

/**
 * Whether text still contains anything capability-shaped.
 *
 * Exported for the tests and for any future reporting path: a check that
 * only runs in tests is a check that stops being true.
 *
 * Fresh regexes rather than the module-level ones above, because those
 * carry the `g` flag for `replace` and `RegExp.prototype.test` on a
 * global regex advances `lastIndex` — the same call would return true,
 * then false, then true, which is the worst possible behaviour for a
 * check that guards a capability.
 */
export function looksLikeSecret(text: string): boolean {
  const value = String(text ?? "");
  return new RegExp(SECRET_RUN.source, "u").test(value)
    || new RegExp(URLISH.source, "iu").test(value)
    || new RegExp(FRAGMENT.source, "u").test(value);
}
